import React, { useEffect, useRef, useState } from "react";
import { Loader2, ShieldX } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import PageLayout from "../PageLayout";
import { SummaryCard } from "../SummaryCard";
import { Claim } from "./types";
import ClaimsTable, { ImagePreview } from "./ClaimsTable";
import { ActionsDropdown, CreateMemberForm, CreateUserForm, Modal } from "./AdminForms";
import { API_BASE_URL } from "../../config/api";

// ─── Access Denied ────────────────────────────────────────────────────────────

const AccessDenied: React.FC = () => {
    const navigate = useNavigate();
    return (
        <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-4">
            <div className="text-center space-y-4 max-w-sm">
                <ShieldX className="w-10 h-10 text-red-500 mx-auto" />
                <div>
                    <h1 className="text-lg font-semibold text-white mb-1">
                        Access Denied
                    </h1>
                    <p className="text-sm text-gray-500">
                        This page is restricted to administrators only.
                    </p>
                </div>
                <button
                    onClick={() => navigate("/")}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded transition-colors"
                >
                    Go to Reporting Dashboard
                </button>
            </div>
        </div>
    );
};

// ─── Service Dashboard ────────────────────────────────────────────────────────

const ServiceDashboard: React.FC = () => {
    const { user, isLoading: authLoading } = useAuth();

    const [claims, setClaims] = useState<Claim[]>([]);
    const [loading, setLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    const [showUserModal, setShowUserModal] = useState(false);
    const [showMemberModal, setShowMemberModal] = useState(false);

    const [previewImages, setPreviewImages] = useState<string[]>([]);
    const [previewIndex, setPreviewIndex] = useState(0);
    const [showPreview, setShowPreview] = useState(false);

    const isAdmin = user?.userName === "omkark@securedstitch.com";

    // ── Fetch claims ──────────────────────────────────────────────────────────

    const fetchClaims = async (refresh = false) => {
        if (refresh) setIsRefreshing(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/claim`);
            const data = await res.json();
            setClaims(data);
        } catch {
            // silent
        } finally {
            setLoading(false);
            setIsRefreshing(false);
        }
    };

    useEffect(() => {
        if (!isAdmin) return;
        fetchClaims();
        intervalRef.current = setInterval(() => fetchClaims(), 900_000);
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [isAdmin]);

    // ── Image preview helpers ─────────────────────────────────────────────────

    const openPreview = (images: string[], i = 0) => {
        setPreviewImages(images);
        setPreviewIndex(i);
        setShowPreview(true);
    };

    const closePreview = () => {
        setShowPreview(false);
        setPreviewImages([]);
    };

    const nextImg = () =>
        setPreviewIndex((p) => (p + 1) % previewImages.length);

    const prevImg = () =>
        setPreviewIndex(
            (p) => (p - 1 + previewImages.length) % previewImages.length
        );

    // ── Derived stats ─────────────────────────────────────────────────────────

    const todayCount = claims.filter(
        (c) =>
            new Date(c.createdAt).toDateString() ===
            new Date().toDateString()
    ).length;

    const pendingCount = claims.filter(
        (c) => c.claimStatus === "REQUESTED"
    ).length;

    const inProgressCount = claims.filter(
        (c) =>
            c.claimStatus === "ACCEPTED_PICKUP" ||
            c.claimStatus === "PRE_QC" ||
            c.claimStatus === "IN_SERVICE" ||
            c.claimStatus === "POST_QC" ||
            c.claimStatus === "DISPATCHED"
    ).length;

    const deliveredCount = claims.filter(
        (c) => c.claimStatus === "DELIVERED"
    ).length;

    const rejectedCount = claims.filter((c) => c.claimStatus === "REJECTED").length;
    const cancelledCount = claims.filter((c) => c.claimStatus === "CANCELLED").length;
    const withReviewsCount = claims.filter((c) => c.rating).length;

    // ── Auth guard ────────────────────────────────────────────────────────────

    if (authLoading) {
        return (
            <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center gap-2 text-gray-400 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading…
            </div>
        );
    }

    if (!isAdmin) return <AccessDenied />;

    // ─────────────────────────────────────────────────────────────────────────

    const summaries = [
        { label: "Total Claims", value: claims.length.toString() },
        { label: "Today", value: todayCount.toString() },
        { label: "Pending", value: pendingCount.toString() },
        { label: "In Progress", value: inProgressCount.toString() },
        { label: "Delivered", value: deliveredCount.toString() },
        { label: "Rejected", value: rejectedCount.toString() },
        { label: "Cancelled", value: cancelledCount.toString() },
        { label: "With Reviews", value: withReviewsCount.toString() },
    ];

    return (
        <>
            <PageLayout
                title="Service Dashboard"
                rightContent={
                    <ActionsDropdown
                        onCreateMember={() => setShowMemberModal(true)}
                        onCreateUser={() => setShowUserModal(true)}
                    />
                }
            >
                {/* ── Stats Cards (Matches SummaryGrid) ───────────────────── */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 w-full mb-5">
                    {summaries.map((s) => (
                        <SummaryCard key={s.label} title={s.label} value={s.value} />
                    ))}
                </div>

                {/* ── Claims Table ─────────────────────────────────────────── */}
                <ClaimsTable
                    claims={claims}
                    loading={loading}
                    isRefreshing={isRefreshing}
                    onRefresh={() => fetchClaims(true)}
                    onImageClick={openPreview}
                />
            </PageLayout>

            {/* ── Admin Modals ─────────────────────────────────────────────── */}
            <Modal
                isOpen={showUserModal}
                onClose={() => setShowUserModal(false)}
                title="Create User"
                wide
            >
                <CreateUserForm onClose={() => setShowUserModal(false)} />
            </Modal>

            <Modal
                isOpen={showMemberModal}
                onClose={() => setShowMemberModal(false)}
                title="Create Member"
                wide
            >
                <CreateMemberForm onClose={() => setShowMemberModal(false)} />
            </Modal>

            {/* ── Image Preview Lightbox ───────────────────────────────────── */}
            {showPreview && (
                <ImagePreview
                    images={previewImages}
                    index={previewIndex}
                    onClose={closePreview}
                    onNext={nextImg}
                    onPrev={prevImg}
                />
            )}

            <style>{`
                input[type="date"]::-webkit-calendar-picker-indicator {
                    filter: invert(0.5);
                    cursor: pointer;
                }
            `}</style>
        </>
    );
};

export default ServiceDashboard;